import { useEffect, useReducer } from "react";
import { useNavigate } from "react-router";
import { Box, Center, Flex, Heading, SimpleGrid, Text, VStack, Tooltip, Checkbox, HStack } from "@chakra-ui/react";
import { MdAttachMoney, MdDirectionsCar, MdOutlineMonitorHeart } from "react-icons/md";
import { FaServer, FaUser } from "react-icons/fa";

import { FirmPro } from "../utils/interfaces";


interface MeterSectionProps {
  allFirmPros: FirmPro[];
  allEnabledFirmProIndices: number[];
  badEnabledFirmProIndices: number[];
  disabledFirmProIndices: number[];
  reloadDeviceIndices: (updateReloadKey: boolean) => void,
};

interface FirmProBoxProps {
  meter: FirmPro;
  setAlarms: () => Promise<void>;
};

enum SelectKey {
  server = "server",  // placeholder que no se muestra en el semáforo
  hardware = "hardware",
  software = "software",
  user = "user",
  commercial = "commercial",
};

interface FilterState {
  server: boolean;  // placeholder que no se muestra en el semáforo
  hardware: boolean;
  software: boolean;
  user: boolean;
  commercial: boolean;
  indices: number[];
}

const getInitialFilterState = (badEnabledFirmProIndices: number[]): FilterState => {
  const maybeStateStr = localStorage.getItem("filtros");
  if (maybeStateStr === null) {
    return {
      server: false,
      hardware: true,
      software: true,
      user: true,
      commercial: false,
      indices: badEnabledFirmProIndices.slice(),
    };
  }
  const state = JSON.parse(maybeStateStr) as FilterState;
  state.indices = badEnabledFirmProIndices.slice();
  return state;
}

/**
 * Muestra primero la información de todos los equipos con fallas (del arreglo
 * badFirmPros) y luego la información de todos los equipos, buenos o con
 * fallas (del arreglo allFirmPros).
 * 
 * Ambos arreglos, allFirmPros y badFirmPros, son arreglos de objetos con la
 * información de cada equipo. Por cada objeto en los arreglos, se despliega
 * una FirmProBox en la página con su información.
 */
export default function FirmProSection({
  allFirmPros,
  allEnabledFirmProIndices,
  badEnabledFirmProIndices,
  disabledFirmProIndices,
  reloadDeviceIndices,
}: MeterSectionProps): JSX.Element {
  const initialState = getInitialFilterState(badEnabledFirmProIndices);
  const reducer = (state: FilterState, action: [SelectKey, boolean]) => {
    const newState = { ...state }
    newState[action[0]] = action[1];
    
    // Recalcular índices.
    const errorTypeIndices = [];
    const keys = Object.keys(SelectKey);
    for (let eti = 0; eti < keys.length; eti++) {
      const selectErrorType = newState[keys[eti] as SelectKey];
      if (selectErrorType) {
        errorTypeIndices.push(eti);
      }
    }

    newState.indices = [];
    for (let firmProIndex of badEnabledFirmProIndices) {
      const subColors = allFirmPros[firmProIndex].subBoxBGColors;
      for (let errorTypeIndex of errorTypeIndices) {
        const subColor = subColors[errorTypeIndex];
        if (subColor.startsWith("red") || subColor.startsWith("yellow")) {
          newState.indices.push(firmProIndex);
          break;
        }
      }
    }

    localStorage.setItem("filtros", JSON.stringify(newState));
    
    return newState;
  };

  const [filterState, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    dispatch([SelectKey.hardware, filterState.hardware]);
  // eslint-disable-next-line
  }, [badEnabledFirmProIndices]);

  function FirmProGrid({ deviceIndices }: { deviceIndices: number[] }): JSX.Element {
    if (deviceIndices.length === 0) {
      return (
        <Text m="auto" py="12" fontSize="3xl" fontWeight="bold" color="gray.300">
          No hay FirmPros seleccionados.
        </Text>
      );
    }
    return (
      <SimpleGrid
        columns={{ "base": 1, "sm": 2, "md": 3, "lg": 4, "xl": 5 }}
        rowGap={{ "base": 2, "xl": 3, "2xl": 4 }}
        columnGap={{ "sm": 3, "xl": 5, "2xl": 8 }}
      >
        {deviceIndices.map(i => {
          if (i >= allFirmPros.length) {
            return null;
          } 
          
          const meter = allFirmPros[i];

          const setAlarms = async () => { };
          // const setAlarms = async () => {
          //   return postSetAlarms(clientID, meterID, !enableAlarms)
          //     .then(() => {
          //       allMeters[i].enableAlarms = !enableAlarms;
          //       reloadDeviceIndices(true);
          //     });
          // };

          return <FirmProBox meter={meter} key={i} setAlarms={setAlarms} />;
        })}
      </SimpleGrid>
    );
  }

  function getKeyChanger(key: SelectKey): (event: React.ChangeEvent<HTMLInputElement>) => void {
    return function changeKey(event: React.ChangeEvent<HTMLInputElement>) {
      dispatch([key, event.target.checked]);
    }
  }

  return (
    <>
      <Heading px="2" as="h3" size={{ "base": "md", "lg": "lg" }}>
        FirmPros con errores y/o alarmas: {badEnabledFirmProIndices.length}
      </Heading>
      <HStack gap="2em">
        <p>Filtrar errores: </p>
        <Checkbox defaultChecked={initialState.hardware} onChange={getKeyChanger(SelectKey.hardware)}>Hardware</Checkbox>
        <Checkbox defaultChecked={initialState.software} onChange={getKeyChanger(SelectKey.software)}>Software</Checkbox>
        <Checkbox defaultChecked={initialState.user} onChange={getKeyChanger(SelectKey.user)}>Usuario</Checkbox>
        <Checkbox defaultChecked={initialState.commercial} onChange={getKeyChanger(SelectKey.commercial)}>Comercial</Checkbox>
      </HStack>
      <FirmProGrid deviceIndices={filterState.indices} />

      <Heading textAlign="center" size={{ "base": "lg", "lg": "xl" }}>
        Todos los FirmPros habilitados: {allEnabledFirmProIndices.length}
      </Heading>
      <FirmProGrid deviceIndices={allEnabledFirmProIndices} />

      {disabledFirmProIndices.length > 0 &&
        <>
          <Heading textAlign="center" size={{ "base": "lg", "lg": "xl" }}>FirmPros deshabilitados: {disabledFirmProIndices.length}</Heading>  
          <FirmProGrid deviceIndices={disabledFirmProIndices} />
        </>
      }
    </>
  )
}

function SubBoxIcon({ index }: { index: number }): JSX.Element {
  switch (index) {
    case 0:
      return <FaServer size="32px" />;
    case 1:
      return <MdDirectionsCar size="32px" />;
    case 2:
      return <MdOutlineMonitorHeart size="32px" />;
    case 3:
      return <FaUser size="32px" />;
    case 4:
      return <MdAttachMoney size="32px" />;
    default:
      return <></>;
  }
}

/**
 * Mestra toda la información relevante de un dispositivo. Cambia
 * de color dependiendo de su estado: más información en el archivo
 * homeScreen/generateMeterData.ts.
 */
function FirmProBox({ meter, setAlarms }: FirmProBoxProps): JSX.Element {
  const navigate = useNavigate();

  // const { isOpen, onOpen, onClose } = useDisclosure();
  // const cancelRef = useRef() as RefObject<HTMLButtonElement>;

  const { nfp, nombre_fp, boxBGColor, boxTime, subBoxBGColors, subBoxTooltips, enableAlarms } = meter;



  return (
    <VStack
      w={{ "base": "250px", "sm": "210px", "md": "218px", "xl": "226px", "2xl": "270px" }}
      spacing={0}
      shadow="lg"
    >
      <Box
        position="relative"
        w="full"
        pt={{ "base": 1, "xl": 2 }}
        pb={{ "base": 2, "xl": 3 }}
        px="2"
        borderTopRadius="lg"
        bg={boxBGColor}
        color={enableAlarms ? "white" : "gray.500"}
        textAlign="center"
      >
        {/*<IconButton
          icon={enableAlarms ? <MoonIcon /> : <BellIcon />}
          aria-label={enableAlarms ? "Desactivar alarma" : "Activar alarma"}
          position="absolute"
          top={{ "base": 3, "2xl": 4 }}
          left={{ "base": 3, "2xl": 5 }}
          size="xs"
          colorScheme={enableAlarms ? "blue" : "yellow"}
          onClick={onOpen}
          shadow="md"
          border="1px"
        />*/}
        <p onClick={() => navigate(`${nfp}`)} style={{ cursor: "pointer", fontWeight: "bold", fontSize: "1.5rem", height: "2.15em", verticalAlign: "middle", lineHeight: "1em", textOverflow: "ellipsis", overflowY: "hidden", display: "block", whiteSpace: "normal" }}>
          {nfp}. {nombre_fp}
        </p>
        <p>
          {boxTime}
        </p>
      </Box>

      {/* MeterSubBox */}
      <Flex w="full" h="54px" borderBottomRadius="lg">
        {[0, 1, 2, 3, 4].map(i => (
          (i === 0 && subBoxBGColors[i] === "white")
          ? null
          : <Tooltip key={i} hasArrow label={subBoxTooltips[i]}>
            <Center
              w="full"
              h="54px"
              bg={subBoxBGColors[i]}
              color={subBoxBGColors[i] === "white" ? "gray.300" : "white"}
              borderBottomLeftRadius={i === 0 || (i === 1 && subBoxBGColors[0] === "white") ? "lg" : "none"}
              borderBottomRightRadius={i === 4 ? "lg" : "none"}
            >
              <SubBoxIcon index={i} />
            </Center>
          </Tooltip>
        ))}
      </Flex>

      {/* AlertDialog */}
      {/*
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {enableAlarms ? "Desactivar" : "Activar"} medidor {nfp}
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Seguro que deseas {enableAlarms && "des"}activar el <b>medidor {nfp}</b>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme={enableAlarms ? "blue" : "yellow"} onClick={() => setAlarms().then(onClose)} ml={3}>
                {enableAlarms ? <MoonIcon mr={2} /> : <BellIcon mr={2} />}
                {enableAlarms ? "Desactivar" : "Activar"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      */}
    </VStack>
  )
}